<template>
  <div class="min-h-screen bg-white">
    <!-- Navigation -->
    <nav class="absolute top-0 left-0 right-0 z-50 bg-transparent">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between items-center h-20">
          <!-- Logo -->
          <div class="flex items-center gap-3">
            <div class="w-12 h-12 bg-gradient-to-br from-cyan-500 to-purple-500 rounded-lg flex items-center justify-center text-white font-bold text-xl shadow-lg shadow-cyan-500/25">
              C
            </div>
            <span class="text-2xl font-bold text-white">ContUp</span>
          </div>

          <!-- Nav Links -->
          <div class="hidden md:flex items-center gap-8">
            <a href="#" class="text-white/90 hover:text-white transition-colors">Home</a>
            <a href="#advantages" class="text-white/90 hover:text-white transition-colors">Avantaje</a>
            <a href="#contact" class="text-white/90 hover:text-white transition-colors">Contact</a>
            <button
              @click="handleLoginClick"
              class="px-6 py-2.5 bg-white text-slate-800 rounded-full hover:bg-slate-100 transition-all duration-300 font-semibold shadow-lg"
            >
              Autentificare
            </button>
          </div>

          <!-- Mobile menu button -->
          <button @click="showMobileMenu = !showMobileMenu" class="md:hidden text-white hover:text-cyan-200">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Mobile Menu Overlay -->
      <transition name="fade">
        <div
          v-if="showMobileMenu"
          class="fixed inset-0 bg-black/50 z-40"
          @click="closeMobileMenu"
        ></div>
      </transition>

      <!-- Mobile Menu Panel -->
      <transition name="slide">
        <div
          v-if="showMobileMenu"
          class="fixed top-0 right-0 h-full w-72 bg-slate-800 z-50 shadow-xl"
        >
          <div class="p-6">
            <!-- Close button -->
            <button @click="closeMobileMenu" class="absolute top-4 right-4 text-white hover:text-cyan-200">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <!-- Logo -->
            <div class="flex items-center gap-3 mb-8">
              <div class="w-10 h-10 bg-gradient-to-br from-cyan-500 to-purple-500 rounded-lg flex items-center justify-center text-white font-bold text-lg shadow-lg shadow-cyan-500/25">
                C
              </div>
              <span class="text-xl font-bold text-white">ContUp</span>
            </div>

            <!-- Nav Links -->
            <nav class="flex flex-col gap-4">
              <a href="#" @click="closeMobileMenu" class="text-white/90 hover:text-white transition-colors py-2">Home</a>
              <a href="#advantages" @click="closeMobileMenu" class="text-white/90 hover:text-white transition-colors py-2">Avantaje</a>
              <a href="#contact" @click="closeMobileMenu" class="text-white/90 hover:text-white transition-colors py-2">Contact</a>
              <button
                @click="handleLoginClick(); closeMobileMenu()"
                class="mt-4 px-6 py-2.5 bg-white text-slate-800 rounded-full hover:bg-slate-100 transition-all duration-300 font-semibold shadow-lg"
              >
                Autentificare
              </button>
            </nav>
          </div>
        </div>
      </transition>
    </nav>

    <!-- Hero Section -->
    <section class="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-800 via-slate-700 to-slate-800 overflow-hidden">
      <!-- Decorative Sphere Top Right -->
      <div class="absolute top-20 right-10 w-72 h-72 md:w-96 md:h-96 bg-gradient-to-br from-cyan-400/40 to-purple-500/40 rounded-full blur-3xl pointer-events-none"></div>

      <!-- Decorative Sphere Bottom Left -->
      <div class="absolute bottom-20 left-10 w-64 h-64 md:w-80 md:h-80 bg-gradient-to-tr from-purple-400/30 to-cyan-400/30 rounded-full blur-3xl pointer-events-none"></div>

      <!-- Small accent sphere -->
      <div class="absolute top-1/3 left-1/4 w-32 h-32 bg-gradient-to-br from-cyan-300/50 to-purple-300/50 rounded-full blur-2xl pointer-events-none"></div>

      <!-- Content -->
      <div class="relative z-10 max-w-4xl mx-auto px-4 text-center py-32">
        <h1 class="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
          Și firmele de contabilitate<br/>
          <span class="bg-gradient-to-r from-cyan-300 to-purple-300 bg-clip-text text-transparent">
            sunt firme!
          </span>
        </h1>
        <p class="text-lg sm:text-xl md:text-2xl text-slate-200 mb-10 max-w-2xl mx-auto font-light leading-relaxed">
          Soluția digitală care te ajută să gestionezi mai bine contabilitatea. Stocare, schimb de informații, documente și rapoarte 100% online.
        </p>
        <button
          @click="handleLoginClick"
          class="px-8 py-4 bg-gradient-to-r from-cyan-500 to-purple-500 text-white rounded-full text-lg font-semibold hover:from-cyan-400 hover:to-purple-400 transition-all duration-300 shadow-xl shadow-cyan-500/30 hover:shadow-purple-500/30"
        >
          Autentificare
        </button>
      </div>
    </section>

    <!-- Credibility Section -->
    <section class="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-cyan-500 to-purple-500">
      <div class="max-w-4xl mx-auto text-center">
        <div class="inline-flex items-center gap-2 px-4 py-2 bg-white/20 rounded-full mb-6">
          <span class="w-2 h-2 bg-white rounded-full animate-pulse"></span>
          <span class="text-white text-sm font-medium">Platformă activă</span>
        </div>
        <h3 class="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
          Peste <span class="text-cyan-100">1.500</span> de firme
        </h3>
        <p class="text-lg sm:text-xl text-white/90">
          Alătură-te comunității de firme de contabilitate care își optimizează munca cu ContUp
        </p>
      </div>
    </section>

    <!-- Free Platform / Pricing Section -->
    <section id="pricing" class="py-20 md:py-24 px-4 sm:px-6 lg:px-8 bg-slate-50 relative overflow-hidden">
      <!-- Decorative spheres -->
      <div class="absolute right-0 top-0 w-64 h-64 md:w-80 md:h-80 bg-cyan-200/30 rounded-full blur-3xl pointer-events-none"></div>
      <div class="absolute left-0 bottom-0 w-48 h-48 md:w-64 md:h-64 bg-purple-200/30 rounded-full blur-3xl pointer-events-none"></div>

      <div class="max-w-2xl mx-auto relative z-10 text-center">
        <h2 class="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-800 mb-12">
          Platformă gratuită pentru firmele de contabilitate
        </h2>

        <!-- Card -->
        <div class="bg-white rounded-3xl p-8 sm:p-12 border border-slate-200 shadow-xl">
          <div class="mb-8">
            <p class="text-5xl sm:text-6xl font-bold bg-gradient-to-r from-cyan-500 to-purple-500 bg-clip-text text-transparent mb-4">
              GRATUIT
            </p>
            <p class="text-lg sm:text-xl text-slate-600">100% gratuit pentru tine și clienții tăi</p>
          </div>

          <div class="space-y-4 mb-8 text-left">
            <div class="flex items-start gap-3">
              <svg class="w-6 h-6 text-cyan-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
              </svg>
              <p class="text-slate-700">Fără costuri ascunse - totul este gratuit</p>
            </div>
            <div class="flex items-start gap-3">
              <svg class="w-6 h-6 text-cyan-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
              </svg>
              <p class="text-slate-700">Conturile clienților tăi sunt complet gratuite</p>
            </div>
            <div class="flex items-start gap-3">
              <svg class="w-6 h-6 text-cyan-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
              </svg>
              <p class="text-slate-700">Începe imediat - fără perioadă de probă limitată</p>
            </div>
            <div class="flex items-start gap-3">
              <svg class="w-6 h-6 text-cyan-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
              </svg>
              <p class="text-slate-700">Acces nelimitat la toate funcționalitățile</p>
            </div>
          </div>

          <button
            @click="handleLoginClick"
            class="w-full px-8 py-4 bg-gradient-to-r from-cyan-500 to-purple-500 text-white rounded-full hover:from-cyan-400 hover:to-purple-400 transition-all duration-300 text-lg font-semibold shadow-lg shadow-cyan-500/25"
          >
            Autentificare Acum
          </button>
        </div>
      </div>
    </section>

    <!-- Implementation Support -->
    <section class="py-20 md:py-24 px-4 sm:px-6 lg:px-8 bg-white">
      <div class="max-w-7xl mx-auto">
        <h2 class="text-2xl sm:text-3xl md:text-4xl font-bold text-center text-slate-800 mb-16">
          SUPORT LA IMPLEMENTARE
        </h2>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
          <div class="text-center">
            <div class="w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-br from-cyan-100 to-purple-100 flex items-center justify-center">
              <svg class="w-8 h-8 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <p class="text-slate-600">Un specialist dedicat se va ocupa exclusiv de setarea conturilor, importul datelor existente și instruirea întregii echipe.</p>
          </div>

          <div class="text-center">
            <div class="w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-br from-cyan-100 to-purple-100 flex items-center justify-center">
              <svg class="w-8 h-8 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </div>
            <p class="text-slate-600">Set complet de tutoriale video pentru toate funcționalitățile din platformă, disponibile oricând atât pentru angajați cât și pentru clienții tăi.</p>
          </div>

          <div class="text-center">
            <div class="w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-br from-cyan-100 to-purple-100 flex items-center justify-center">
              <svg class="w-8 h-8 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
            <p class="text-slate-600">Primești suport pe toată perioada implementării din partea unui specialist, precum și asistență tehnică gratuită post-implementare.</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Features Section -->
    <section id="advantages" class="py-20 md:py-24 px-4 sm:px-6 lg:px-8 bg-slate-50">
      <div class="max-w-7xl mx-auto">
        <h2 class="text-2xl sm:text-3xl md:text-4xl font-bold text-center text-slate-800 mb-4">
          Funcționalități complete pentru firmele de contabilitate
        </h2>
        <p class="text-center text-slate-600 mb-16 text-lg max-w-2xl mx-auto">
          Tot ce ai nevoie pentru a gestiona eficient relația cu clienții tăi
        </p>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <!-- Communication -->
          <div class="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm hover:shadow-lg hover:border-cyan-200 transition-all duration-300 group">
            <div class="w-12 h-12 bg-gradient-to-br from-cyan-100 to-purple-100 rounded-xl flex items-center justify-center mb-4 group-hover:from-cyan-200 group-hover:to-purple-200 transition-all">
              <svg class="w-6 h-6 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-slate-800 mb-2">Comunicare centralizată</h3>
            <p class="text-slate-600">Comunici eficient cu toți clienții pe o singură platformă, fără email sau telefon.</p>
          </div>

          <!-- Document Management -->
          <div class="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm hover:shadow-lg hover:border-cyan-200 transition-all duration-300 group">
            <div class="w-12 h-12 bg-gradient-to-br from-cyan-100 to-purple-100 rounded-xl flex items-center justify-center mb-4 group-hover:from-cyan-200 group-hover:to-purple-200 transition-all">
              <svg class="w-6 h-6 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-slate-800 mb-2">Gestionare documente</h3>
            <p class="text-slate-600">Gestionezi și arhivezi digital toate documentele contabile într-un singur loc.</p>
          </div>

          <!-- Document Expiration -->
          <div class="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm hover:shadow-lg hover:border-cyan-200 transition-all duration-300 group">
            <div class="w-12 h-12 bg-gradient-to-br from-cyan-100 to-purple-100 rounded-xl flex items-center justify-center mb-4 group-hover:from-cyan-200 group-hover:to-purple-200 transition-all">
              <svg class="w-6 h-6 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-slate-800 mb-2">Notificări automate</h3>
            <p class="text-slate-600">Clienții primesc notificări la expirarea documentelor - zero întârzieri.</p>
          </div>

          <!-- Profitability Tracking -->
          <div class="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm hover:shadow-lg hover:border-cyan-200 transition-all duration-300 group">
            <div class="w-12 h-12 bg-gradient-to-br from-cyan-100 to-purple-100 rounded-xl flex items-center justify-center mb-4 group-hover:from-cyan-200 group-hover:to-purple-200 transition-all">
              <svg class="w-6 h-6 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-slate-800 mb-2">Profitabilitate per client</h3>
            <p class="text-slate-600">Calculezi profitabilitatea pentru fiecare client în parte, în timp real.</p>
          </div>

          <!-- Task Management -->
          <div class="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm hover:shadow-lg hover:border-cyan-200 transition-all duration-300 group">
            <div class="w-12 h-12 bg-gradient-to-br from-cyan-100 to-purple-100 rounded-xl flex items-center justify-center mb-4 group-hover:from-cyan-200 group-hover:to-purple-200 transition-all">
              <svg class="w-6 h-6 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-slate-800 mb-2">Management sarcini echipă</h3>
            <p class="text-slate-600">Centralizezi sarcinile echipei și vezi statusul fiecăreia în timp real.</p>
          </div>

          <!-- Time Tracking -->
          <div class="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm hover:shadow-lg hover:border-cyan-200 transition-all duration-300 group">
            <div class="w-12 h-12 bg-gradient-to-br from-cyan-100 to-purple-100 rounded-xl flex items-center justify-center mb-4 group-hover:from-cyan-200 group-hover:to-purple-200 transition-all">
              <svg class="w-6 h-6 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-slate-800 mb-2">Monitorizare timp</h3>
            <p class="text-slate-600">Măsori timpul petrecut pe activități și identifici sarcinile neproductive.</p>
          </div>

          <!-- Cloud Backup -->
          <div class="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm hover:shadow-lg hover:border-cyan-200 transition-all duration-300 group">
            <div class="w-12 h-12 bg-gradient-to-br from-cyan-100 to-purple-100 rounded-xl flex items-center justify-center mb-4 group-hover:from-cyan-200 group-hover:to-purple-200 transition-all">
              <svg class="w-6 h-6 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-slate-800 mb-2">Backup automat în cloud</h3>
            <p class="text-slate-600">Backup live 100% pentru toate datele - nu pierzi niciodată informații.</p>
          </div>

          <!-- Security -->
          <div class="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm hover:shadow-lg hover:border-cyan-200 transition-all duration-300 group">
            <div class="w-12 h-12 bg-gradient-to-br from-cyan-100 to-purple-100 rounded-xl flex items-center justify-center mb-4 group-hover:from-cyan-200 group-hover:to-purple-200 transition-all">
              <svg class="w-6 h-6 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-slate-800 mb-2">Securitate maximă</h3>
            <p class="text-slate-600">Securitatea și confidențialitatea datelor este garantată 100%.</p>
          </div>

          <!-- Access Anywhere -->
          <div class="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm hover:shadow-lg hover:border-cyan-200 transition-all duration-300 group">
            <div class="w-12 h-12 bg-gradient-to-br from-cyan-100 to-purple-100 rounded-xl flex items-center justify-center mb-4 group-hover:from-cyan-200 group-hover:to-purple-200 transition-all">
              <svg class="w-6 h-6 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-slate-800 mb-2">Acces de oriunde</h3>
            <p class="text-slate-600">Datele sunt în cloud - acces de pe orice dispozitiv, oriunde, oricând.</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Testimonials Section -->
    <section class="py-20 md:py-24 px-4 sm:px-6 lg:px-8 bg-white relative overflow-hidden">
      <div class="max-w-7xl mx-auto relative z-10">
        <h2 class="text-2xl sm:text-3xl md:text-4xl font-bold text-center text-slate-800 mb-4">
          Ce spun clienții noștri
        </h2>
        <p class="text-center text-slate-600 mb-16 text-lg">
          Păreri de la firme de contabilitate care folosesc ContUp
        </p>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          <!-- Testimonial 1 -->
          <div class="bg-slate-50 rounded-2xl p-8 border border-slate-200">
            <div class="flex items-center gap-1 mb-4">
              <svg class="w-5 h-5 text-amber-400" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>
              <svg class="w-5 h-5 text-amber-400" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>
              <svg class="w-5 h-5 text-amber-400" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>
              <svg class="w-5 h-5 text-amber-400" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>
              <svg class="w-5 h-5 text-amber-400" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>
            </div>
            <p class="text-slate-600 mb-6 italic leading-relaxed">
              "ContUp ne-a transformat complet modul de lucru. Comunicarea cu clienții este acum mult mai eficientă, iar documentele sunt organizate impecabil."
            </p>
            <div class="flex items-center gap-4">
              <div class="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-500 to-purple-500 flex items-center justify-center text-white font-bold">
                MP
              </div>
              <div>
                <p class="font-semibold text-slate-800">Maria Popescu</p>
                <p class="text-sm text-slate-500">Director</p>
              </div>
            </div>
          </div>

          <!-- Testimonial 2 -->
          <div class="bg-slate-50 rounded-2xl p-8 border border-slate-200">
            <div class="flex items-center gap-1 mb-4">
              <svg class="w-5 h-5 text-amber-400" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>
              <svg class="w-5 h-5 text-amber-400" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>
              <svg class="w-5 h-5 text-amber-400" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>
              <svg class="w-5 h-5 text-amber-400" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>
              <svg class="w-5 h-5 text-amber-400" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>
            </div>
            <p class="text-slate-600 mb-6 italic leading-relaxed">
              "De când folosim platforma, timpul petrecut pe comunicare s-a redus cu 50%. Clienții sunt mai mulțumiți și noi lucrăm mai organizat."
            </p>
            <div class="flex items-center gap-4">
              <div class="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-cyan-500 flex items-center justify-center text-white font-bold">
                AI
              </div>
              <div>
                <p class="font-semibold text-slate-800">Andrei Ionescu</p>
                <p class="text-sm text-slate-500">Administrator</p>
              </div>
            </div>
          </div>

          <!-- Testimonial 3 -->
          <div class="bg-slate-50 rounded-2xl p-8 border border-slate-200">
            <div class="flex items-center gap-1 mb-4">
              <svg class="w-5 h-5 text-amber-400" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>
              <svg class="w-5 h-5 text-amber-400" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>
              <svg class="w-5 h-5 text-amber-400" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>
              <svg class="w-5 h-5 text-amber-400" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>
              <svg class="w-5 h-5 text-amber-400" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>
            </div>
            <p class="text-slate-600 mb-6 italic leading-relaxed">
              "Recomandăm ContUp tuturor firmelor de contabilitate. Platforma este intuitivă și suportul tehnic este excepțional. Și cel mai bun lucru? Este gratuit!"
            </p>
            <div class="flex items-center gap-4">
              <div class="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-400 to-purple-400 flex items-center justify-center text-white font-bold">
                ED
              </div>
              <div>
                <p class="font-semibold text-slate-800">Elena Dumitrescu</p>
                <p class="text-sm text-slate-500">Partener</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Benefits Section -->
    <section class="py-20 md:py-24 px-4 sm:px-6 lg:px-8 bg-slate-50 relative overflow-hidden">
      <!-- Decorative spheres -->
      <div class="absolute left-0 top-1/4 w-48 h-48 md:w-64 md:h-64 bg-cyan-200/40 rounded-full blur-3xl pointer-events-none"></div>
      <div class="absolute right-0 bottom-1/4 w-64 h-64 md:w-96 md:h-96 bg-purple-200/40 rounded-full blur-3xl pointer-events-none"></div>

      <div class="max-w-7xl mx-auto relative z-10">
        <h2 class="text-2xl sm:text-3xl md:text-4xl font-bold text-center text-slate-800 mb-16">
          Beneficiile clienților tăi
        </h2>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto">
          <div class="flex items-start gap-4 p-4 rounded-xl bg-white border border-slate-200">
            <svg class="w-6 h-6 text-cyan-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
            </svg>
            <p class="text-slate-700">Fără întârzieri, primesc notificări la expirarea documentelor.</p>
          </div>

          <div class="flex items-start gap-4 p-4 rounded-xl bg-white border border-slate-200">
            <svg class="w-6 h-6 text-cyan-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
            </svg>
            <p class="text-slate-700">Nu pierd informații, backup live 100% a tuturor datelor.</p>
          </div>

          <div class="flex items-start gap-4 p-4 rounded-xl bg-white border border-slate-200">
            <svg class="w-6 h-6 text-cyan-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
            </svg>
            <p class="text-slate-700">Stau fără griji. Securitatea și confidențialitatea datelor este asigurată.</p>
          </div>

          <div class="flex items-start gap-4 p-4 rounded-xl bg-white border border-slate-200">
            <svg class="w-6 h-6 text-cyan-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
            </svg>
            <p class="text-slate-700">Disponibilă oriunde. Datele sunt în cloud, accesibile oricând.</p>
          </div>

          <div class="flex items-start gap-4 p-4 rounded-xl bg-white border border-slate-200">
            <svg class="w-6 h-6 text-cyan-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
            </svg>
            <p class="text-slate-700">Trimit și primesc documentele rapid. Reduc timpul alocat schimburilor de email.</p>
          </div>

          <div class="flex items-start gap-4 p-4 rounded-xl bg-white border border-slate-200">
            <svg class="w-6 h-6 text-cyan-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
            </svg>
            <p class="text-slate-700">Primesc acces la întreaga listă de servicii auxiliare oferite.</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Contact Section -->
    <section id="contact" class="py-20 md:py-24 px-4 sm:px-6 lg:px-8 bg-white">
      <div class="max-w-7xl mx-auto">
        <h2 class="text-2xl sm:text-3xl md:text-4xl font-bold text-center text-slate-800 mb-4">
          Contactează-ne
        </h2>
        <p class="text-center text-slate-600 mb-12 max-w-xl mx-auto">
          Suntem aici să te ajutăm. Sună-ne sau scrie-ne un email și îți răspundem rapid.
        </p>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-2xl mx-auto">
          <!-- Phone -->
          <a
            href="tel:0741711594"
            class="group flex flex-col items-center text-center gap-4 p-8 bg-white border border-slate-200 rounded-2xl shadow-sm hover:shadow-lg hover:border-cyan-300 hover:-translate-y-1 transition-all duration-300"
          >
            <div class="w-14 h-14 bg-gradient-to-br from-cyan-100 to-purple-100 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
              <svg class="w-7 h-7 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
            </div>
            <div>
              <p class="text-sm font-medium text-slate-500 mb-1">Telefon</p>
              <span class="text-lg font-semibold text-slate-800 group-hover:text-cyan-600 transition-colors">0741 711 594</span>
            </div>
          </a>

          <!-- Email -->
          <a
            href="mailto:office@kontto.ro"
            class="group flex flex-col items-center text-center gap-4 p-8 bg-white border border-slate-200 rounded-2xl shadow-sm hover:shadow-lg hover:border-cyan-300 hover:-translate-y-1 transition-all duration-300"
          >
            <div class="w-14 h-14 bg-gradient-to-br from-cyan-100 to-purple-100 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
              <svg class="w-7 h-7 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <p class="text-sm font-medium text-slate-500 mb-1">Email</p>
              <span class="text-lg font-semibold text-slate-800 group-hover:text-cyan-600 transition-colors">office@kontto.ro</span>
            </div>
          </a>
        </div>
      </div>
    </section>

    <!-- Footer -->
    <footer class="bg-slate-800 text-white pt-12 pb-8 px-4 sm:px-6 lg:px-8">
      <div class="max-w-7xl mx-auto">
        <!-- Logo and tagline -->
        <div class="flex flex-col items-center mb-8">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-10 h-10 bg-gradient-to-br from-cyan-500 to-purple-500 rounded-lg flex items-center justify-center text-white font-bold shadow-lg shadow-cyan-500/25">
              C
            </div>
            <span class="text-xl font-bold text-white">ContUp</span>
          </div>
          <p class="text-slate-400 text-sm">Platforma digitală pentru firmele de contabilitate</p>
        </div>

        <!-- Footer Links -->
        <div class="flex justify-center gap-6 text-sm mb-8">
          <a href="#" class="text-slate-400 hover:text-slate-200 transition-colors">Termeni și condiții</a>
          <a href="#" class="text-slate-400 hover:text-slate-200 transition-colors">Confidențialitate</a>
          <a href="#" class="text-slate-400 hover:text-slate-200 transition-colors">Cookie-uri</a>
        </div>

        <div class="text-center text-slate-500 text-sm border-t border-slate-700 pt-8">
          <p>&copy; Copyright 2026 ContUp. Un produs digital.</p>
          <p class="mt-2">
            <a href="https://xolasolutions.com" target="_blank" rel="noopener noreferrer" class="text-slate-400 hover:text-slate-200 transition-colors">
              Website realizat de Xola Solutions
            </a>
          </p>
        </div>
      </div>
    </footer>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const router = useRouter()
const authStore = useAuthStore()

const isAuthenticated = computed(() => authStore.isAuthenticated)

const showMobileMenu = ref(false)

const closeMobileMenu = () => {
  showMobileMenu.value = false
}

const handleLoginClick = () => {
  if (isAuthenticated.value) {
    router.push({ name: 'dashboard' })
  } else {
    router.push({ name: 'login' })
  }
}
</script>

<style scoped>
/* Mobile menu transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(100%);
}
</style>
